<template>
  <div class="review-summary-layout">
    <el-card shadow="never" class="panel-card review-notes-card">
      <div class="review-notes">
        <el-input
          v-model="reviewNotesProxy"
          type="textarea"
          :rows="2"
          placeholder="Add overall notes for this review"
        ></el-input>
      </div>

      <div v-if="!regions.length" class="review-confirmation mt-14">
        <el-checkbox v-model="cleanCycleConfirmedProxy">
          Sensor values looks clean throughout the pad cycle
        </el-checkbox>
      </div>

      <div class="action-row mt-14">
        <el-button
          type="primary"
          class="submit-button"
          @click="$emit('submit-review')"
        >
          Save and Continue
        </el-button>
      </div>
    </el-card>

    <el-card shadow="never" class="panel-card selected-regions-card">
      <div class="detail-label mb-8">
        Selected regions ({{ regions.length }})
      </div>
      <div v-if="regions.length" class="region-list">
        <RegionSummaryCard
          v-for="region in regions"
          :key="region.id"
          :region="region"
          :format-region-range-fn="formatRegionRangeFn"
          :format-region-timestamp-fn="formatRegionTimestampFn"
          :timestamp-cycle="timestampCycle"
          :format-reason-code-fn="formatReasonCodeFn"
          :collapsible="true"
          :expanded="isRegionExpanded(region.id)"
          :show-remove="true"
          :show-sensor-tag="false"
          :show-sensor-row="true"
          :show-timestamp-header="false"
          :show-timestamp-row="true"
          @toggle="toggleRegionExpanded"
          @remove="$emit('remove-region', $event)"
        />
      </div>
      <div v-else class="empty-inline">
        No regions selected. If everything looks fine, save and continue.
      </div>
    </el-card>
  </div>
</template>

<script>
import RegionSummaryCard from '@/components/labelling/RegionSummaryCard'

export default {
  name: 'ReviewDecisionPanel',
  components: {
    RegionSummaryCard
  },
  data() {
    return {
      expandedRegionIds: []
    }
  },
  props: {
    reviewNotes: {
      type: String,
      default: ''
    },
    cleanCycleConfirmed: {
      type: Boolean,
      default: false
    },
    regions: {
      type: Array,
      default: () => []
    },
    timestampCycle: {
      type: Object,
      default: null
    },
    formatRegionTimestampFn: {
      type: Function,
      required: true
    },
    formatRegionRangeFn: {
      type: Function,
      required: true
    },
    formatReasonCodeFn: {
      type: Function,
      required: true
    }
  },
  computed: {
    reviewNotesProxy: {
      get() {
        return this.reviewNotes
      },
      set(value) {
        this.$emit('update:reviewNotes', value)
      }
    },
    cleanCycleConfirmedProxy: {
      get() {
        return this.cleanCycleConfirmed
      },
      set(value) {
        this.$emit('update:cleanCycleConfirmed', value)
      }
    }
  },
  methods: {
    isRegionExpanded(regionId) {
      return this.expandedRegionIds.indexOf(regionId) > -1
    },
    toggleRegionExpanded(regionId) {
      if (this.isRegionExpanded(regionId)) {
        this.expandedRegionIds = this.expandedRegionIds.filter(
          item => item !== regionId
        )
        return
      }
      this.expandedRegionIds = [...this.expandedRegionIds, regionId]
    }
  }
}
</script>

<style lang="scss" scoped>
.review-summary-layout {
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(280px, 360px);
  gap: 12px;
  align-items: start;
}

.review-notes-card :deep(.el-card__body),
.selected-regions-card :deep(.el-card__body) {
  padding: 14px 16px;
}

.detail-label {
  font-size: 12px;
  color: var(--text2);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.empty-inline {
  color: var(--text2);
  line-height: 1.6;
}

.review-notes {
  display: grid;
  min-width: 0;
}

.review-notes :deep(.el-textarea__inner) {
  min-height: 88px !important;
}

.review-confirmation {
  color: var(--text);
}

.review-confirmation :deep(.el-checkbox) {
  display: flex;
  align-items: flex-start;
  line-height: 1.5;
}

.review-confirmation :deep(.el-checkbox__label) {
  padding-left: 10px;
  color: var(--text);
  font-size: 14px;
  line-height: 1.5;
  white-space: normal;
}

.selected-regions-card {
  max-height: 250px;
  min-width: 0;
}

.selected-regions-card :deep(.el-card__body) {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
}

.region-list {
  display: grid;
  gap: 6px;
  max-height: 140px;
  padding-right: 4px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.action-row :deep(.submit-button) {
  min-height: 58px;
  width: 100%;
  padding: 0 32px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 10px 24px rgba(34, 197, 94, 0.22);
}

@media (max-width: 1200px) {
  .review-summary-layout {
    grid-template-columns: 1fr;
  }
}
</style>
